<template>
  <div class="col-12 col-xl-12">

    <template v-if="$route.name !== 'subject-grade-sheets.view'">
      <div class="card mb-3 mb-xxl-5 me-xxl-4" style="margin-top: 4rem; overflow: visible">
        <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
          <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
                {{ t("grade_sheets.title_for_subject") }}: {{ subject?.name || t("gradeSheets.loading") }}
              </h6>
            </div>
          </div>
        </div>

        <div class="card-body px-0 pb-2 pe-xl-3">
          <div class="table-responsive p-1 p-xl-4">
            <LoadingSpinner v-if="loading" :message="t('gradeSheets.loading')" class="py-5" />

            <template v-else>
              <!-- meta block -->
              <div v-if="gradeSheets.length" class="d-flex justify-content-between mb-4 mx-3 print-header">
                <div>
                  <h6 class="mb-1">
                    {{ t("gradeSheets.class") }}:
                    <span :class="{ 'text-muted': !gradeSheets[0]?.class }">
                      {{ gradeSheets[0]?.class?.name || t("gradeSheets.loading") }}
                    </span>
                  </h6>
                  <h6 class="mb-1">
                    {{ t("gradeSheets.subject") }}:
                    <span :class="{ 'text-muted': !subject }">
                      {{ subject?.name || t("gradeSheets.loading") }}
                    </span>
                  </h6>
                </div>

                <div class="text-end">
                  <h6 class="mb-1">
                    {{ t("gradeSheets.academic_year") }}:
                    <span :class="{ 'text-muted': !gradeSheets[0]?.academic_year }">
                      {{ gradeSheets[0]?.academic_year?.name || gradeSheets[0]?.academic_year?.year || t("gradeSheets.loading") }}
                      <span v-if="gradeSheets[0]?.academic_year?.is_active" class="badge bg-success ms-1">
                        {{ t("common.current") }}
                      </span>
                    </span>
                  </h6>

                  <h6 class="mb-1">
                    {{ t("gradeSheets.createdAt") }}:
                    <span :class="{ 'text-muted': !gradeSheets[0]?.created_at }">
                      {{ gradeSheets[0]?.created_at ? formatDate(gradeSheets[0].created_at) : t("gradeSheets.loading") }}
                    </span>
                  </h6>
                </div>
              </div>

              <!-- list -->
              <div v-if="gradeSheets.length === 0" class="text-center py-5">
                <i class="fas fa-file-alt fa-2x text-muted mb-3"></i>
                <h6 class="text-muted">{{ t("gradeSheets.noSheets") }}</h6>
              </div>

              <div v-else class="list-group">
                <router-link
                  v-for="sheet in gradeSheets"
                  :key="sheet.id"
                  :to="{
                    name: 'subject-grade-sheets.view',
                    params: { subjectId: route.params.subjectId, id: sheet.id },
                    query: { readonly: '1' }
                  }"
                  class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                >
                  <div>
                    <h6 class="mb-1">{{ sheet.title }}</h6>
                    <small class="text-muted">
                      {{ t("gradeSheets.createdAt") }}: {{ formatDate(sheet.created_at) }}
                    </small>
                  </div>
                  <div><i class="fas fa-chevron-left"></i></div>
                </router-link>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>

    <!-- ✅ CHILD PAGE ONLY: render it alone (it already has its own header) -->
    <template v-else>
      <router-view />
    </template>

  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import api from '@/api';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';

const { t } = useI18n();
const route = useRoute();
const toast = useToast();

const loading = ref(true);
const subject = ref(null);
const gradeSheets = ref([]);

const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('ar-LY', options);
};

const fetchSubject = async () => {
  try {
    const response = await api.get(`/subjects/${route.params.subjectId}`);
    subject.value = response.data.data;
  } catch (error) {
    toast.error(t('gradeSheets.failedLoadSubject'));
    console.error(error);
  }
};

const fetchGradeSheets = async () => {
  try {
    const response = await api.get('/grade-sheets', {
      params: { subject_id: route.params.subjectId }
    });
    gradeSheets.value = response.data.data || [];
  } catch (error) {
    toast.error(t('gradeSheets.failedLoadSheets'));
    console.error(error);
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  await fetchSubject();
  await fetchGradeSheets();
});
</script>
